import { supabase } from '@/integrations/supabase/client';

export interface LiteratureHit {
  id: string;
  title: string;
  authors: string[];
  year: number | null;
  venue: string | null;
  doi: string | null;
  url: string | null;
  abstract: string | null;
  citation_count: number | null;
}

export interface SearchLiteratureArgs {
  query: string;
  limit?: number;
}

/**
 * Search the scholarly literature via the Writer edge function.
 * The upstream call is proxied server-side so no user identity or
 * manuscript content ever reaches the third-party API.
 */
export async function searchLiterature(
  args: SearchLiteratureArgs,
): Promise<LiteratureHit[]> {
  const query = (args.query ?? '').trim();
  if (!query) return [];
  const { data, error } = await supabase.functions.invoke(
    'writer-search-literature',
    {
      body: { query, limit: args.limit ?? 8 },
    },
  );
  if (error) throw new Error(error.message ?? 'Literature search failed');
  const results = (data as { results?: LiteratureHit[] } | null)?.results;
  return Array.isArray(results) ? results : [];
}

/** Format a hit as a compact BibTeX-ish citation for pasting into a draft. */
export function formatCitationLine(hit: LiteratureHit): string {
  const authors = hit.authors.length
    ? hit.authors.slice(0, 3).join(', ') +
      (hit.authors.length > 3 ? ' et al.' : '')
    : 'Unknown';
  const year = hit.year ? ` (${hit.year})` : '';
  const venue = hit.venue ? `. ${hit.venue}` : '';
  const doi = hit.doi ? `. doi:${hit.doi}` : '';
  return `${authors}${year}. ${hit.title}${venue}${doi}`.trim();
}
